import React, { Component } from 'react'

import { Loading } from '@fielded/shared-ui'
import { productAliasesByMarket, translateTerritoryAlias } from '@fielded/fs-api/lib/product/tools'
import { SHIPMENT_STATUS } from '@fielded/fs-api/lib/shipment/constants'
import { batchIdToProductId } from '@fielded/fs-api/lib/batch/tools'
import { getProductsForFollowUp } from '@fielded/fs-api/lib/shipment/tools'

import withConfig from '../../../van-shared/hoc/withConfig'
import withShipment from '../common/WithShipmentWrapper'
import { withUser } from '../../../common/AuthenticationProvider'
import { hasFeature } from '../../../van-shared/utils/features'
import { isReturnShipment } from '../../../common/utils/shipment'
import { isFpUser, isPsmBasedApp, isPsmDriverUser, isPsmResellerFacilityUser } from '../common/utils'
import withOffline from '@fielded/shared-ui/src/common/offline'

import Complete from './Complete'
import Partial from './Partial'

// TODO: this should be removed, if no longer needed
// adding this because some shipments on dev and staging
// don't have the new snapshot and this will break the workflow
export const getFirstSnapshot = (shipment) => {
  const snapShotDateList = Object.keys(shipment.snapshotDates)
  let originSnapshot = snapShotDateList[0]

  for (let i = 1; i <= snapShotDateList.length; i++) {
    if (shipment.snapshotDates[snapShotDateList[i]] <= shipment.snapshotDates[originSnapshot]) {
      originSnapshot = snapShotDateList[i]
    }
  }
  return originSnapshot
}
class CompleteContainer extends Component {
  constructor (props) {
    super(props)
    this.state = {
      isLoading: true,
      skipped: false,
      updatedNewSnapShot: null
    }
  }

  async componentDidMount () {
    const { api, shipment, user, isOnline } = this.props
    const isDriver = isPsmDriverUser(user)

    // For offline drivers, we can skip fetching extra data that is used for logic they don't see (Partial view)
    // This prevents blocking the UI if API calls fail or take too long
    if (isDriver && !isOnline) {
      this.setState({
        isLoading: false
      })
      return
    }

    try {
      let originLocation = await api.location.get(shipment.origin.id)
      let destinationLocation = await api.location.get(shipment.destination.id)
      if (!destinationLocation) {
        const locations = await api.location.getLocationsViaIds([shipment.destination.id])
        destinationLocation = locations[0]
      }
      if (!originLocation) {
        const locations = await api.location.getLocationsViaIds([shipment.origin.id])
        originLocation = locations[0]
      }

      const updatedNewSnapShot = await this.fetchProductAlias(destinationLocation)
      this.setState({
        isLoading: false,
        originLocation,
        destinationLocation,
        updatedNewSnapShot
      })
    } catch (error) {
      console.warn('Error loading complete container data', error)
      // Even if fetching auxiliary data (locations, product aliases)
      // fails—which is expected in offline scenarios—the core
      // shipment data is already available via props. We deliberately
      // catch the error and disable the loading state here to ensure
      // the user can still see the delivery completion
      // screen and isn't blocked by an infinite spinner.
      this.setState({
        isLoading: false
      })
    }
  }

  createResupplyOrder = async () => {
    this.setState({ confirmLoading: true })
    const { api, shipment, history } = this.props
    const response = await api.order.createFollowUpOrder(shipment)
    const groupId = response[0].groupId
    const programs = await api.program.list()
    const programId = programs[0].id
    this.setState({ confirmLoading: false })
    history.push(`/orders/${programId}/products-detail/${groupId}`)
  }

  appendResupplyShipment = async () => {
    this.setState({ confirmLoading: true })
    const { api, shipment, history } = this.props

    const productIds = Object.keys(shipment.counts).reduce((acc, batchId) => {
      return [...acc, batchIdToProductId(batchId)]
    }, [])

    const allProducts = await api.product.getProductsViaIds(productIds)

    const location = this.state.originLocation

    const { translatedCounts } = await api.shipment.bulkTranslateShipmentProducts({shipment, location, products: allProducts})

    const snapshot = await api.shipment.createFollowUpShipment(shipment, translatedCounts)

    this.setState({ confirmLoading: false })
    window.localStorage.setItem('newDelivery', JSON.stringify(snapshot))
    history.push(`/shipments/list`)
  }

  fetchProductAlias = async (destinationLocation) => {
    const { api, shipment } = this.props
    let productIds = []
    const firstSnapshot = getFirstSnapshot(shipment)
    const newSnapshot = Object.entries(shipment.history).find(([key]) => (
      key.includes(':status:new:') || key.includes(`:status:${firstSnapshot}:`)
    ))[1]
    if (!newSnapshot) {
      throw new Error('No new snapshot found in shipment history')
    }

    const destinationService = (destinationLocation && destinationLocation.services) ? destinationLocation.services[0] : null

    // Collect product IDs from the new snapshot
    if (shipment.status === SHIPMENT_STATUS.RECEIVED) {
      productIds = Object.keys(newSnapshot.counts).reduce((acc, batchId) => {
        return [...acc, batchIdToProductId(batchId)]
      }, [])

      if (productIds.length === 0) {
        throw new Error('No product IDs found')
      }

      try {
        const products = await api.product.getProductsViaIds(productIds)
        if (!products || products.length === 0) {
          throw new Error('No products returned from API')
        }

        // Create a mapping of product IDs to their market aliases
        const productsForTranslation = productAliasesByMarket(products)
        const productAliasMapping = {}

        productIds.forEach(p => {
          const product = batchIdToProductId(p)
          const alias = translateTerritoryAlias(product, productsForTranslation, destinationService)
          productAliasMapping[p] = alias
        })

        // Update the skus in newSnapshot with the market aliases. Because the new snapshots carry the central packpoint id and getProductsForFollowUp uses skus in both new and recieved snaps to find the diff
        const updatedCounts = Object.keys(newSnapshot.counts).reduce((acc, key) => {
          const productId = batchIdToProductId(key)
          const alias = productAliasMapping[productId] || productId
          const newKey = key.replace(productId, alias) // The newKey assignment replaces the original product ID in the batch ID (key) with its corresponding market alias.
          acc[newKey] = newSnapshot.counts[key]
          return acc
        }, {})

        // Return the updated newSnapshot with replaced keys
        return {
          ...newSnapshot,
          counts: updatedCounts
        }
      } catch (error) {
        console.error('Error fetching product aliases:', error)
        throw error
      }
    }
  }

  handleSkip = () => {
    this.setState({ skipped: true })
  }

  render () {
    const {
      config,
      shipment,
      user,
      location,
      rootHistory,
      isOnline
    } = this.props

    const {
      isLoading,
      originLocation,
      destinationLocation,
      skipped,
      updatedNewSnapShot
    } = this.state

    if (isLoading) {
      return <Loading />
    }
    const isAdjustment = hasFeature(config.features, 'shipments.adjustments') && shipment.status === 'received'
    const negativeChanges = getProductsForFollowUp(shipment, updatedNewSnapShot)
    const isSupplierShipment = originLocation && originLocation.level === 'supplier'
    const isPackPointShipment = originLocation && destinationLocation && originLocation.level === 'pack-point' && destinationLocation.level === 'pack-point'
    const isSDPShipment = originLocation && destinationLocation && destinationLocation.level === 'sdp' && originLocation.level === 'pack-point'
    const isSent = shipment.status === 'sent'
    const isReturn = isReturnShipment(user, shipment)
    const isFP = isFpUser(user)
    const isPsmResellerFacility = isPsmResellerFacilityUser(config, user)

    const isDeliveryPartiallyFulfilled = (
      !skipped && !!negativeChanges.length && !isFP && !isPsmResellerFacility && (isSupplierShipment || isSDPShipment || isPackPointShipment)
    )

    let pathName = `${location.pathname}/print/all-statements`
    let noteName = 'delivery note'
    let packageNotePathName
    if (isReturn) {
      pathName = `/shipments/returns-note/${shipment.id}`
      noteName = 'returns note'
    } else if (isSent) {
      noteName = 'package note'
    } else if (isPsmBasedApp(config)) {
      pathName = `/shipments/last-mile-deliveries/delivery-statement/${shipment.snapshotId}`
      packageNotePathName = `/shipments/last-mile-deliveries/package-note/${shipment.snapshotId}`
    }

    return isDeliveryPartiallyFulfilled
      ? (
        <Partial
          isAdjustment={isAdjustment}
          createResupplyOrder={this.createResupplyOrder}
          appendResupplyShipment={this.appendResupplyShipment}
          negativeChanges={negativeChanges}
          onSkip={this.handleSkip}
        />
      )
      : (
        <Complete
          noteName={noteName}
          printPathname={pathName}
          isAdjustment={isAdjustment}
          rootHistory={rootHistory}
          isPsmResellerFacility={isPsmResellerFacility}
          packageNotePathName={packageNotePathName}
          isOnline={isOnline}
        />
      )
  }
}

export default withOffline(withConfig(withUser(withShipment(CompleteContainer))))
